import React, { useState } from "react";
import { Container, Button, Form, Card, Modal, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import "react-datepicker/dist/react-datepicker.css";
import { useMutation } from "@apollo/client";
import { CREATE_SITE_MEASUREMENT } from "../../../../graphql/mutations";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { SiteLevelMeasurements, SourceLevelMeasurements, DetectionTechnology, EnvironmentalConditions } from "./sections";
import { PreliminaryReconciliationAssessment } from "./sections/PreliminaryReconciliationAssessment";

const AddSiteMeasurementPage = ({
  versionsCategories = [],
  ogmpCategories = [],
  measurementTech = [],
  siteNames = [],
  currentGroupVersion,
  currentTenant,
  env,
  handleEnvChange
}) => {
  const navigate = useNavigate();
  const [createSiteMeasurement, { loading: mutationLoading }] = useMutation(CREATE_SITE_MEASUREMENT);

  const [siteData, setSiteData] = useState({
    siteId: "",
    measurementDateTime: new Date(),
    startTime: new Date(),
    endTime: new Date(),
    emissions: "",
    emissionsUnit: "kgch4/hr",
    siteLevelUncertainty: "",
    sourceLevelUncertainty: "",
    minimumDetectionLimit: "",
    minimumDetectionUnit: "",
    summary: "",
    reconciliationStatus: "PENDING"
  });

  const siteNameOptions = siteNames
    ? siteNames.map((siteName) => ({
        value: siteName.id,
        label: siteName.name
      }))
    : [];

  const [sources, setSources] = useState([
    { id: 1, sourceCategoryId: null, emissions: "", emissionsUnit: "kgch4/hr", summary: "", versionOgmpCategoryId: "" }
  ]);

  const [techData, setTechData] = useState({
    technologyId: "",
    provider: "",
    detectionThreshold: "",
    spatialResolution: ""
  });

  const [envData, setEnvData] = useState({
    windSpeed: "",
    windSpeedUnit: "m/s",
    temperature: "",
    temperatureUnit: "celsius",
    environmentalUncertainty: ""
  });

  const currentTenantName = localStorage.getItem("selectedTenantName") || null;

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const mockMeasurementTech = [
    { id: "tech1", name: "Optical Gas Imaging (OGI)" },
    { id: "tech2", name: "Laser Detection And Ranging (LDAR)" },
    { id: "tech3", name: "Differential Absorption Lidar (DIAL)" },
    { id: "tech4", name: "Satellite Monitoring" },
    { id: "tech5", name: "Aerial Surveys" },
    { id: "tech6", name: "Continuous Monitoring Systems" },
    { id: "tech7", name: "Flux Chambers" },
    { id: "tech8", name: "Mobile Survey Units" }
  ];

  // calculate reconciliation status
  const calculateReconciliationStatus = () => {
    if (siteData.reconciliationStatus !== "READY") {
      return "DATA_PENDING";
    }

    const siteEmissions = parseFloat(siteData.emissions || 0);
    const siteLevelUncertainty = parseFloat(siteData.siteLevelUncertainty || 0);
    const sourceLevelUncertainty = parseFloat(siteData.sourceLevelUncertainty || 0);

    // calculate total emissions from all sources
    const totalSourceEmissions = sources
      .filter((source) => source.sourceCategoryId && source.emissions)
      .reduce((sum, source) => sum + parseFloat(source.emissions || 0), 0);

    const site_start = (1 - siteLevelUncertainty) * siteEmissions;
    const site_end = (1 + siteLevelUncertainty) * siteEmissions;

    const source_start = (1 - sourceLevelUncertainty) * totalSourceEmissions;
    const source_end = (1 + sourceLevelUncertainty) * totalSourceEmissions;

    if (site_start <= source_end && source_start <= site_end) {
      return "RECONCILED";
    } else {
      return "NOT_RECONCILED";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // check if this is a reconciled measurement and show warning on first attempt
    const reconciliationStatus = calculateReconciliationStatus();
    if (reconciliationStatus === "RECONCILED" && !submitAttempted) {
      setSubmitAttempted(true);
      setShowWarningModal(true);
      return;
    }
    await addSiteMeasurement();
  };

  const addSiteMeasurement = async () => {
    const sourceMeasurementsPayload = sources.map((source) => ({
      emissions: Number(source.emissions),
      emissionsUnit: source.emissionsUnit,
      summary: source.summary,
      versionOgmpCategoryId: source.versionOgmpCategoryId
    }));

    const siteMeasurementInputPayload = {
      projectId: siteData.siteId,
      emissions: Number(siteData.emissions),
      emissionsUnit: siteData.emissionsUnit,
      measurementTechnologyId: techData.technologyId ? techData.technologyId : null,
      measurementProvider: techData.provider,
      detectionThreshold: techData.detectionThreshold,
      spatialResolution: techData.spatialResolution,
      groupVersionId: currentGroupVersion,
      measurementTimestamp: siteData.measurementDateTime,
      startTime: siteData.startTime.toISOString().split("T")[1],
      endTime: siteData.endTime.toISOString().split("T")[1],
      windSpeed: Number(envData.windSpeed),
      windSpeedUnit: envData.windSpeedUnit,
      temperature: Number(envData.temperature),
      temperatureUnit: envData.temperatureUnit,
      environmentalUncertainty: envData.environmentalUncertainty,
      summary: siteData.summary,
      uncertaintyFraction: Number(siteData.siteLevelUncertainty),
      sourceLevelUncertaintyFraction: Number(siteData.sourceLevelUncertainty),
      sourceMeasurements: sourceMeasurementsPayload,
      reconciliationReadyStatus: siteData.reconciliationStatus,
      minimumDetectionLimit: siteData.minimumDetectionLimit === "" ? null : Number(siteData.minimumDetectionLimit),
      minimumDetectionUnit: siteData.minimumDetectionUnit ? siteData.minimumDetectionUnit : null
    };
    try {
      const { data } = await createSiteMeasurement({
        variables: {
          tenantId: currentTenant,
          siteMeasurementInput: siteMeasurementInputPayload
        }
      });
      if (data) {
        toast.success(`Site Measurement created successfully with ID: ${data?.createSiteMeasurement}`);
      } else {
        toast.error("Error creating tenant");
      }
    } catch {
      setTimeout(() => {
        toast.error("Failed to add new site measurements");
      }, 200);
    } finally {
      handleEnvChange(env, currentTenant, currentTenantName, currentGroupVersion);
      navigate("/measurements");
    }
  };

  return (
    <Container className="mt-1">
      <h4 className="mb-4">Add New Site Measurement</h4>
      <Card className="border mb-3" style={{ maxHeight: "800px", overflowY: "auto" }}>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <SiteLevelMeasurements
              siteData={siteData}
              setSiteData={setSiteData}
              siteOptions={siteNameOptions}
              sources={sources}
              setSources={setSources}
            />

            <SourceLevelMeasurements
              sources={sources}
              setSources={setSources}
              versionsCategories={versionsCategories}
              ogmpCategories={ogmpCategories}
            />

            <DetectionTechnology
              techData={techData}
              setTechData={setTechData}
              measurementTech={measurementTech?.length ? measurementTech : mockMeasurementTech}
            />

            <EnvironmentalConditions envData={envData} setEnvData={setEnvData} />

            <PreliminaryReconciliationAssessment siteData={siteData} sources={sources} />

            <div className="d-grid">
              <Button variant="primary" type="submit" className="mt-3 mb-3 mx-auto" style={{ width: "25%" }} disabled={mutationLoading}>
                {mutationLoading ? "Creating measurement..." : "Submit Measurement"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Warning Modal for Reconciled Measurements */}
      <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
            Reconciled Measurement Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <p>
              <strong>This measurement has been preliminarily marked as RECONCILED.</strong>
            </p>
            <p>
              This means that the site-level measurements and source-level measurements overlap within their respective uncertainty ranges,
              which is a positive indicator of data quality.
            </p>
            <p>Please review all entered data to ensure accuracy before final submission.</p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowWarningModal(false);
              setSubmitAttempted(false);
            }}
          >
            Review Data
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowWarningModal(false);
              setSubmitAttempted(false);
              addSiteMeasurement();
            }}
          >
            Confirm & Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddSiteMeasurementPage;
